import { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Box, Chip,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { RowAction } from '../../components/data-grid/RouteDataGrid';
import { apiFetch, apiFetchBlob } from '../../api/client';
import { useAccessToken } from '../../auth/useAccessToken';
import type { TerminalSessionRow } from '../../api/queries';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';

type Row = TerminalSessionRow & { id: string };

const stateColor: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  active: 'success', pending: 'info', ended: 'default', killed: 'warning', expired: 'warning', denied: 'error',
};

/**
 * Cross-SC remote-terminal session log with recording playback. Reused on the
 * Site controllers → Terminal sessions page AND the Audit log. RLS-scoped server
 * side (ACES all; operators their own SCs).
 */
export function TerminalSessionsPanel() {
  const getToken = useAccessToken();
  const [play, setPlay] = useState<Row | null>(null);

  const columns: GridColDef<Row>[] = [
    { field: 'qr_id', headerName: 'Site controller', width: 200,
      renderCell: (p) => p.row.qr_id ? <code>{p.row.qr_id}</code> : '— (no QR Code)' },
    { field: 'requested_by', headerName: 'Opened by', width: 180,
      renderCell: (p) => fmtOrEmDash(p.row.requested_by) },
    { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 160,
      renderCell: (p) => fmtOrEmDash(p.row.reason) },
    { field: 'state', headerName: 'State', width: 110,
      renderCell: (p) => <Chip size="small" label={p.row.state} color={stateColor[p.row.state] ?? 'default'} /> },
    { field: 'started_at', headerName: 'Started', width: 180,
      renderCell: (p) => fmtDateTime(p.row.started_at) },
    { field: 'duration_seconds', headerName: 'Duration', width: 110,
      renderCell: (p) => p.row.duration_seconds != null ? `${p.row.duration_seconds}s` : '—' },
    { field: 'has_recording', headerName: 'Recording', width: 120,
      renderCell: (p) => p.row.has_recording ? 'Yes' : '—' },
  ];

  const actions: RowAction<Row>[] = [
    { label: 'View recording', visible: (r) => r.has_recording, onClick: (r) => setPlay(r) },
  ];

  return (
    <>
      <RouteDataGrid<Row>
        entityName="terminal session"
        queryKey={['terminal-sessions']}
        listFn={async () =>
          (await apiFetch<TerminalSessionRow[]>('/api/v1/terminal/sessions', {}, getToken))
            .map((r) => ({ ...r, id: r.session_id }))}
        columns={columns}
        exportColumns={[
          { key: 'qr_id', label: 'Site controller' },
          { key: 'requested_by', label: 'Opened by' },
          { key: 'reason', label: 'Reason' },
          { key: 'state', label: 'State' },
          { key: 'started_at', label: 'Started' },
          { key: 'duration_seconds', label: 'Duration (s)' },
          { key: 'has_recording', label: 'Recording', format: (v) => (v ? 'yes' : 'no') },
        ]}
        reportTitle="Remote terminal sessions"
        actions={actions}
        emptyMessage="No remote terminal sessions yet."
      />
      <RecordingDialog row={play} onClose={() => setPlay(null)} />
    </>
  );
}

function RecordingDialog({ row, onClose }: { row: Row | null; onClose: () => void }) {
  const getToken = useAccessToken();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    let term: Terminal | null = null;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const blob = await apiFetchBlob(`/api/v1/terminal/sessions/${row.session_id}/recording`, getToken);
        const text = await blob.text();
        requestAnimationFrame(() => {
          if (cancelled || !hostRef.current) return;
          term = new Terminal({
            disableStdin: true, convertEol: false, fontSize: 13,
            fontFamily: 'Menlo, Consolas, "DejaVu Sans Mono", monospace',
            theme: { background: '#0b0f14' },
          });
          const fit = new FitAddon();
          term.loadAddon(fit);
          term.open(hostRef.current);
          fit.fit();
          term.write(text);
        });
      } catch (e) {
        setError((e as { message?: string }).message ?? 'Failed to load recording.');
      }
    })();
    return () => { cancelled = true; try { term?.dispose(); } catch { /* ignore */ } };
  }, [row, getToken]);

  const download = async () => {
    if (!row) return;
    const blob = await apiFetchBlob(`/api/v1/terminal/sessions/${row.session_id}/recording`, getToken);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `terminal-${row.session_id}.log`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={row !== null} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Session recording{row?.qr_id ? ` — ${row.qr_id}` : ''}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 1 }}>Replay of the recorded session output (read-only).</Alert>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Box ref={hostRef} sx={{ height: 460, width: '100%', bgcolor: '#0b0f14', borderRadius: 1, p: 1 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={download} disabled={!row}>Download .log</Button>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
